import { useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link, useLocation } from "wouter";
import SiteLayout from "@/components/SiteLayout";
import { track } from "@/lib/track";
import { applySeo } from "@/lib/seo";
import { setJsonLd, organizationSchema, websiteSchema, buildFaqSchema } from "@/lib/schema";
import {
  MarketingHero,
  MarketingCTA,
} from "@/components/marketing";
import JumpLinks from "@/components/marketing/JumpLinks";
import RelatedInsights from "@/components/RelatedInsights";
import {
  Shield,
  Users,
  Building2,
  Phone,
  ArrowRight,
  Lock,
  Scale,
  FileText,
  UserCheck,
  Check,
  Minus,
  Radar,
} from "lucide-react";

const comparisonOptions = [
  {
    title: "Do it yourself",
    price: "Free",
    summary:
      "You document everything yourself, where wallets are, how keys are stored, and what your family should do.",
    strengths: [
      "No ongoing cost, and nothing shared with anyone",
      "Can work well if your heirs are technical and your instructions stay current",
    ],
    limitations: [
      "Single point of failure, if instructions are lost, outdated, or misunderstood, assets can be permanently unrecoverable",
      "No independent verification of who is acting, or when",
      "Often breaks at handover, the person following the plan is rarely the person who wrote it",
    ],
  },
  {
    title: "Solicitor only",
    price: "Standard will-writing fees",
    summary:
      "A legally sound will and estate plan, drawn up by a regulated professional, essential whatever else you do.",
    strengths: [
      "Legal authority over who inherits, within the established probate process",
      "The right place for every other part of your estate",
    ],
    limitations: [
      "Keys can't go in a will, wills become public at probate",
      "A will says who inherits crypto, not how anyone actually reaches it",
      "No technical recovery capability if access is lost",
    ],
  },
  {
    title: "Bitzo + your solicitor",
    price: "From £19/month",
    summary:
      "Human-led verification, Policy Pack documentation, and recovery coordination, working alongside your will and solicitor, not instead of them.",
    strengths: [
      "Verified trusted contacts, recorded-call verification, and full audit trails",
      "Documentation written for non-technical executors and professionals",
      "Non-custodial, Bitzo never holds keys, seed phrases, or funds",
    ],
    limitations: [
      "An ongoing membership rather than a one-off document",
      "Not legal, financial, or tax advice, you still need a will and solicitor",
    ],
  },
];

const homeFaqs = [
  {
    question: "What does Bitzo actually do?",
    answer:
      "Bitzo is a UK-based, non-custodial coordination service for crypto security and inheritance. We verify your trusted contacts, document your wallet structure and recovery workflow in a Policy Pack, monitor your documented addresses with the Bitcoin Watchtower, and coordinate the human-led recovery process when it is needed, with an audit trail throughout.",
  },
  {
    question: "What is the Bitcoin Watchtower?",
    answer:
      "Watch-only monitoring of Bitcoin addresses you choose, run from Bitzo's own node. If coins ever move from a monitored wallet, you get an email straight away, often before the transaction confirms, giving you the earliest possible warning of theft or unexpected activity. It uses public addresses only and never touches your keys.",
  },
  {
    question: "Do you ever hold my crypto or seed phrases?",
    answer:
      "No. Bitzo never holds, stores, or requests seed phrases, private keys, wallet PINs, or exchange passwords. Your assets remain in your own wallets, under your control, at all times.",
  },
  {
    question: "What happens when I die or lose access?",
    answer:
      "A verified trusted contact, such as your executor or a family member, triggers a recovery case. A UK-based Bitzo specialist verifies their identity and authority through a recorded call, then guides them through the documented recovery workflow. Every step is logged for a complete audit trail.",
  },
  {
    question: "Do I still need a will and solicitor?",
    answer:
      "Yes. Bitzo works alongside your will and solicitor, not instead of them, and we do not provide legal, financial, or tax advice. Because wills become public at probate, keys and recovery details should never go in one, Bitzo provides the documented, verified recovery process your will and solicitor can reference.",
  },
  {
    question: "How much does it cost?",
    answer:
      "Plans start at £19 per month for Core Continuity, with Continuity Plus at £49 and Private Client at £149. Every plan includes Client Portal access, a versioned Policy Pack, verified trusted contacts, review prompts every 4 to 6 months, and the recovery activation pathway. Bitcoin Watchtower monitoring is included with Continuity Plus (up to 3 addresses) and Private Client (up to 20). One thing to know up front: if a recovery is ever triggered, a separate one-off execution fee applies (10% of the value recovered, minimum £999, capped at £20,000), paid from the estate at that point, never while you are alive. See the pricing page for worked examples.",
  },
];

export default function Home() {
  const [, navigate] = useLocation();

  useEffect(() => {
    applySeo({
      title: "Bitzo | Crypto Security & Crypto Inheritance Planning (UK)",
      description: "UK-based crypto security and crypto inheritance planning. Non-custodial coordination, verified contacts, recovery workflows, audit trails. No seed phrases.",
      path: "/"
    });
    setJsonLd("jsonld-org", organizationSchema);
    setJsonLd("jsonld-website", websiteSchema);
    setJsonLd("jsonld-faq-home", buildFaqSchema(homeFaqs));
  }, []);

  const warmBooking = (() => {
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const add = (rel: string, href: string, crossOrigin?: string) => {
        const link = document.createElement("link");
        link.rel = rel;
        link.href = href;
        if (crossOrigin) link.crossOrigin = crossOrigin;
        document.head.appendChild(link);
      };
      add("dns-prefetch", "https://calendly.com");
      add("dns-prefetch", "https://assets.calendly.com");
      add("preconnect", "https://calendly.com");
      add("preconnect", "https://assets.calendly.com", "anonymous");
    };
  })();

  const openBooking = () => {
    track("booking_click", { page: "/" });
    navigate("/book");
  };

  return (
    <SiteLayout>
      <div className="w-full bg-background relative overflow-hidden">

        {/* HERO */}
        <MarketingHero
          mobileImage="/assets/images/home_hero_mobile.webp"
          desktopImage="/assets/images/home_hero.webp"
          badge={{ text: "Crypto security & inheritance planning · UK" }}
          title="Your crypto shouldn't die with you"
          subtitle="Bitzo is a UK-based, non-custodial coordination service for crypto security and inheritance. We help private clients, families, and professional advisers make sure crypto is recoverable when it matters, without us ever holding keys, seed phrases, or funds."
          testId="heading-home"
          priority
        >
          <MarketingCTA delay={0.05}>
            <Button
              size="lg"
              onPointerEnter={warmBooking}
              onFocus={warmBooking}
              onTouchStart={warmBooking}
              onClick={() => { warmBooking(); openBooking(); }}
              className="bg-primary text-background hover:bg-primary/90 font-bold px-8 h-12 text-base"
              data-testid="button-book-call"
            >
              <Phone className="mr-2 h-5 w-5" />
              Book a 20-minute call
            </Button>
            <Link href="/how-it-works">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 h-12 text-base" data-testid="button-how-it-works">
                See how it works
              </Button>
            </Link>
          </MarketingCTA>

          {/* Trust chips */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="flex flex-wrap justify-center gap-2 mt-6"
          >
            {["Plans from £19/month", "Human-led", "UK-based team", "Non-custodial", "Audit-ready"].map((label) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-800/60 border border-white/10 text-xs text-slate-300"
                data-testid={`chip-${label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Check size={10} className="text-primary" />
                {label}
              </span>
            ))}
          </motion.div>
        </MarketingHero>

        {/* TASK 2, JumpLinks navigation */}
        <div className="py-6 border-b border-white/5 bg-background">
          <div className="container mx-auto px-6">
            <JumpLinks
              links={[
                { label: "Who it's for", href: "#who-its-for" },
                { label: "How we work together", href: "#how-we-work" },
                { label: "FAQ", href: "#faq" },
              ]}
              delay={0}
            />
          </div>
        </div>

        {/* TASK 3A, Explore anchor paragraph */}
        <div className="py-5 border-b border-white/5 bg-card/20">
          <div className="container mx-auto px-6">
            <p className="text-sm text-muted-foreground text-center max-w-3xl mx-auto">
              Explore{" "}
              <Link href="/security" className="text-primary hover:underline">crypto security</Link>,{" "}
              <Link href="/inheritance" className="text-primary hover:underline">crypto inheritance planning</Link>, and{" "}
              <Link href="/how-it-works" className="text-primary hover:underline">how crypto inheritance works</Link>.
              {" "}See our{" "}
              <Link href="/crypto-inheritance-planning-uk" className="text-primary hover:underline">UK crypto inheritance guide</Link>{" "}
              and{" "}
              <Link href="/crypto-inheritance-checklist-uk" className="text-primary hover:underline">crypto inheritance checklist</Link>.
            </p>
          </div>
        </div>

        {/* TASK 4A, Human-led, not algorithm-led */}
        <section className="py-20 bg-card/30 border-b border-white/5">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Human-led, not algorithm-led</h2>
                <p className="text-white/80 text-sm leading-relaxed mb-8">
                  If you die or lose access, your family will not be talking to a chatbot. They will be
                  talking to a real UK-based Bitzo specialist who knows your plan, who can verify identity
                  through a recorded call, and who can coordinate with your solicitor, executor, or IFA.
                  We use software where it helps (the Client Portal, the Policy Pack, audit trails), but
                  the recovery itself is run by humans, on purpose. In an era where AI does most things,
                  this is one process where a person on the phone is the point.
                </p>
                <ul className="space-y-4">
                  {[
                    { lead: "Real UK-based specialists", body: "who are never chatbots and never offshore call centres." },
                    { lead: "Recorded video or phone verification", body: "at every step in recovery." },
                    { lead: "Direct coordination", body: "with your IFA, solicitor, accountant, or family office." },
                    { lead: "No algorithmic decisions", body: "about your inheritance, your access, or your contacts." },
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -16 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.07 }}
                      className="flex items-start gap-3 list-none"
                      data-testid={`human-led-${i}`}
                    >
                      <Check size={16} className="text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-white/80 text-sm leading-relaxed">
                        <strong className="text-white">{item.lead}</strong>{" "}{item.body}
                      </p>
                    </motion.li>
                  ))}
                </ul>

                {/* Founder strip: put a real, named face where the "real specialist" promise is made */}
                <div className="mt-10 flex items-center gap-4 rounded-xl border border-white/10 bg-background/40 p-4">
                  <img
                    src="/assets/images/simon-bumford.jpg"
                    alt="Simon Bumford, founder of Bitzo"
                    width={56}
                    height={56}
                    loading="lazy"
                    className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                    data-testid="img-founder-strip"
                  />
                  <p className="text-white/80 text-sm leading-relaxed">
                    <strong className="text-white">Bitzo is founder-led.</strong>{" "}
                    You will deal with real people, starting with founder Simon Bumford, who has worked
                    with bitcoin and self-custody since 2015.{" "}
                    <Link href="/about" className="text-primary hover:underline">Meet the team</Link>.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* WHAT BITZO DOES */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">What Bitzo does</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                We coordinate the people, documentation, and verification needed to recover crypto under planned and unplanned circumstances. Our service is built around four pillars:
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {[
                {
                  icon: Shield,
                  title: "Crypto security planning",
                  body: "We map your setup and make it recoverable, so no single lost device, forgotten password, or accident can strand your coins. We never touch your keys.",
                  href: "/security",
                },
                {
                  icon: Users,
                  title: "Crypto inheritance planning",
                  body: "Verified trusted contacts, executor workflows, and documented recovery so digital assets are not lost on death or incapacity.",
                  href: "/inheritance",
                },
                {
                  icon: Radar,
                  title: "Bitcoin Watchtower",
                  body: "Watch-only monitoring from our own node. An email the moment coins move, often before the transaction confirms. No keys, ever.",
                  href: "/watchtower",
                },
                {
                  icon: Building2,
                  title: "Adviser support",
                  body: "Verification, documentation, and coordination for IFAs, accountants, and solicitors. No advice given. No custody taken.",
                  href: "/advisers",
                },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`pillar-${i}`}
                >
                  <Card className="bg-background/50 border-white/5 h-full hover:border-primary/30 transition-colors">
                    <CardContent className="pt-6">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                        <item.icon size={24} />
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-4">{item.body}</p>
                      <Link href={item.href} className="inline-flex items-center text-primary text-sm font-medium hover:underline">
                        Learn more<span className="sr-only"> about {item.title.toLowerCase()}</span> <ArrowRight size={16} className="ml-1" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO BITZO IS FOR, TASK 5: id="who-its-for" already present */}
        <section id="who-its-for" className="py-20 bg-card/30 border-y border-white/5 scroll-mt-24">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Who Bitzo is for</h2>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { icon: Lock, text: "UK private clients holding meaningful crypto positions who want to reduce single points of failure." },
                { icon: Users, text: "Families and named executors who need a process that works without crypto expertise." },
                { icon: Building2, text: "Professional advisers (IFAs, STEP solicitors, accountants) whose clients hold crypto but who do not want to take operational risk." },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4"
                  data-testid={`who-${i}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0">
                    <item.icon size={24} />
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed pt-2">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* HOW BITZO IS DIFFERENT */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">How Bitzo is different</h2>
                <ul className="space-y-6">
                  {[
                    { icon: Lock, lead: "Non-custodial by design.", body: "We never hold, store, or request seed phrases, private keys, wallet PINs, or exchange passwords. Assets remain under client control at all times." },
                    { icon: Scale, lead: "UK-focused.", body: "Designed around UK probate and HMRC reporting requirements. Bitzo is not authorised or regulated by the FCA, and we do not provide financial, legal, or tax advice. See our Risk Disclosure." },
                    { icon: FileText, lead: "Audit-ready documentation.", body: "Every step, every verification, every recovery action is logged, so your executor and their professionals have a clear record to rely on." },
                    { icon: UserCheck, lead: "Human-led coordination.", body: "Real UK-based specialists run the process, not a bot, not an algorithm." },
                  ].map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.08 }}
                      className="flex items-start gap-4 list-none"
                      data-testid={`diff-${i}`}
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary flex-shrink-0 mt-0.5">
                        <item.icon size={20} />
                      </div>
                      <p className="text-white/80 text-sm leading-relaxed">
                        <strong className="text-white">{item.lead}</strong>{" "}{item.body}
                      </p>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* THE FIVE-STEP WORKFLOW, TASK 5: id="how-we-work" */}
        <section id="how-we-work" className="py-20 bg-card/30 border-y border-white/5 scroll-mt-24">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">The five-step workflow</h2>
              <ol className="space-y-6">
                {[
                  { title: "Architecture", body: "Map current wallets, custody points, and access paths." },
                  { title: "Verified contacts", body: "Identify and verify trusted people (family, executors, advisers)." },
                  { title: "Documentation", body: "Produce a Policy Pack non-technical executors can actually use." },
                  { title: "Recovery rehearsal", body: "Test the workflow before it is needed in real life." },
                  { title: "Annual review", body: "Keep the plan current as wallets, contacts, and rules change." },
                ].map((item, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.07 }}
                    className="flex items-start gap-5 list-none"
                    data-testid={`step-${i + 1}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center text-primary font-bold flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="pt-1">
                      <p className="text-white/80 text-sm leading-relaxed">
                        <strong className="text-white">{item.title}</strong>, {item.body}
                      </p>
                    </div>
                  </motion.li>
                ))}
              </ol>
              {/* TASK 6, correct tier names */}
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="mt-10 text-sm text-muted-foreground"
              >
                Read the full{" "}
                <Link href="/how-it-works" className="text-primary hover:underline">how it works guide</Link>
                {" "}or see{" "}
                <Link href="/pricing" className="text-primary hover:underline">pricing for the Core Continuity, Continuity Plus, and Private Client plans</Link>.
              </motion.p>
            </motion.div>
          </div>
        </section>

        {/* COMMON QUESTIONS (FAQ), TASK 5: id="faq" already present */}
        <section id="faq" className="py-20 scroll-mt-24">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">Common questions</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {[
                  {
                    q: "Will Bitzo ever ask for my seed phrase?",
                    a: "No. Bitzo never asks for seed phrases, private keys, wallet PINs, or exchange passwords under any circumstances.",
                  },
                  {
                    q: "Is Bitzo a wallet, exchange, or custodian?",
                    a: "No. Bitzo is a coordination and documentation service. Your crypto remains in your own wallets at all times.",
                  },
                  {
                    q: "Do you give financial or tax advice?",
                    a: "No. We coordinate verification and process. Regulated advice should come from your IFA, solicitor, or accountant.",
                  },
                  {
                    q: "Can my family handle this if they are not technical?",
                    a: "Yes, that is the design goal. Documentation is written for non-technical executors and professionals.",
                  },
                ].map((item, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-white/10" data-testid={`faq-${i}`}>
                    <AccordionTrigger className="text-white text-left hover:no-underline hover:text-primary">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </div>
        </section>

        {/* THREE WAYS COMPARISON */}
        <section id="compare" className="py-20 bg-card/30 border-y border-white/5 scroll-mt-24">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Three ways people handle crypto inheritance</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Each approach is legitimate. The honest question is what happens on the day someone who is not you has to follow the plan.
              </p>
            </motion.div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {comparisonOptions.map((option, i) => (
                <motion.div
                  key={option.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`compare-${i}`}
                >
                  <Card className="bg-background/50 border-white/5 h-full">
                    <CardContent className="pt-6 flex flex-col h-full">
                      <h3 className="text-lg font-semibold text-white mb-1">{option.title}</h3>
                      <p className="text-primary text-sm font-medium mb-3">{option.price}</p>
                      <p className="text-muted-foreground text-sm leading-relaxed mb-5">{option.summary}</p>
                      <p className="text-xs font-semibold uppercase tracking-wide text-white/60 mb-2">Strengths</p>
                      <ul className="space-y-2 mb-5">
                        {option.strengths.map((item, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <Check size={14} className="text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-white/80 text-sm leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-xs font-semibold uppercase tracking-wide text-white/60 mb-2">Limitations</p>
                      <ul className="space-y-2">
                        {option.limitations.map((item, j) => (
                          <li key={j} className="flex items-start gap-2">
                            <Minus size={14} className="text-slate-500 flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center text-sm text-muted-foreground mt-8 max-w-2xl mx-auto"
            >
              A will drawn up with your solicitor remains essential whichever route you take. Bitzo is
              non-custodial and does not provide legal, financial, or tax advice, see{" "}
              <Link href="/pricing" className="text-primary hover:underline">pricing</Link> or{" "}
              <Link href="/how-it-works" className="text-primary hover:underline">how it works</Link>.
              {" "}Want the detail? <Link href="/compare" className="text-primary hover:underline">See how Bitzo compares with Casa, Unchained and others</Link>.
            </motion.p>
          </div>
        </section>

        {/* CRYPTO INHERITANCE FAQS */}
        <section id="home-faqs" className="py-20 scroll-mt-24">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-10">Crypto inheritance FAQs</h2>
              <div className="space-y-4">
                {homeFaqs.map((faq, i) => (
                  <Card key={i} className="bg-background/50 border-white/5" data-testid={`home-faq-${i}`}>
                    <CardContent className="pt-6">
                      <h3 className="text-white font-semibold mb-2">{faq.question}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-24 bg-gradient-to-b from-background to-card/50 border-t border-white/5">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Talk to a UK specialist</h2>
              <p className="text-muted-foreground mb-10 max-w-xl mx-auto">
                If you hold crypto and want a process that works for your family, executors, or advisers, the next step is a short discovery call. No pressure, no obligation.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  onPointerEnter={warmBooking}
                  onFocus={warmBooking}
                  onTouchStart={warmBooking}
                  onClick={() => { warmBooking(); openBooking(); }}
                  className="bg-primary text-background hover:bg-primary/90 font-bold px-8 h-12 text-base w-full sm:w-auto"
                  data-testid="button-cta-call"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Book a 20-minute call
                </Button>
                <Link href="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 px-8 h-12 text-base w-full sm:w-auto" data-testid="button-cta-contact">
                    Send a message
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        <RelatedInsights title="Insights" />
      </div>
    </SiteLayout>
  );
}
